import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { assetsOrgAssetsCreate } from '@goldcast/api/content';
import { useClipAsset } from '../useClipAsset';
import { remotionPlayerStateStore } from '../../ClipNewTimeline/store/remotionPlayerState';
import { AddMenuItem } from '../../ClipNewTimeline/TimelinePlayerActionBar/AddMenu/types';
import { setIsAddingImage, timelineActionsStore } from '../../ClipNewTimeline/store/timelineActions';
import { ASSET_PATH } from '../constants';
import { useMultiAssetUploader } from '@/components/molecules/MultiAssetUploader/useMultiAssetUploader';
import { IMAGE_FORMATS } from '@/components/molecules/MultiAssetUploader/constants';
import { currentUser } from '@/stores/user';
import { getRelativePlayerTime } from '@/App/remotion/MainPlayer/utils/deletesUtils';
import { useAppContext } from '@/context/AppContext/AppContext';
import { showErrorToast, showSuccessToast } from '@/libs/toast/toast';
import { trackClipUpdate } from '@/stores/clip';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';

interface UseImageOverlayProps {
  onUploadComplete?: () => void;
}

interface UseImageOverlayReturn {
  // Core upload functionality
  uploadImage: (files: File[]) => Promise<void>;
  acceptedFormats: string;

  // Timeline integration
  addImageOverlayMenuItem: AddMenuItem;
}

/**
 * Custom hook for image overlay functionality
 * Provides unified upload logic for both popup and timeline flows
 */
export const useImageOverlay = ({ onUploadComplete }: UseImageOverlayProps): UseImageOverlayReturn => {
  const { fps, clipId } = useClipsContext();

  const { addClipAsset } = useClipAsset();
  const { logger } = useAppContext();
  const { isAddingImage } = useSyncExternalStore(timelineActionsStore.subscribe, timelineActionsStore.getSnapshot);

  const organization = currentUser.getSnapshot()?.organization;
  const baseFilePath = useMemo(() => `${organization}/clip-asset`, [organization]);

  // Calculate current frame from player state or use provided frame
  const getCurrentFrame = useCallback((): number => {
    const currentTime = remotionPlayerStateStore.getSnapshot().currentTime;
    return getRelativePlayerTime({ currentTime, deleteBounds: [] }) * fps - 1;
  }, [fps]);

  // Core business logic for processing uploaded assets
  const processUploadedAssets = useCallback(
    async (validFiles: any[], frame: number) => {
      try {
        await assetsOrgAssetsCreate({
          body: [
            {
              name: validFiles[0].filename,
              source_url: validFiles[0].url,
              type: 'IMAGE'
            }
          ],
          queryParams: {}
        });

        addClipAsset({
          currentFrame: frame,
          asset: validFiles[0],
          type: 'image'
        });

        trackClipUpdate(clipId, 'IMAGE_OVERLAY');
        showSuccessToast('Image uploaded successfully');
      } catch (error) {
        logger.error(error);
        showErrorToast('Failed to upload image');
        throw error;
      }
    },
    [addClipAsset, clipId, logger]
  );

  const { uploadFiles, acceptedFormats } = useMultiAssetUploader({
    baseFilePath,
    assetPath: ASSET_PATH,
    formats: IMAGE_FORMATS,
    handleUploadedAssets: async ({ validFiles }) => {
      if (validFiles.length > 0) {
        const frame = getCurrentFrame();
        await processUploadedAssets(validFiles, frame);
      }
    },
    onError: error => {
      logger.error(error);
      showErrorToast('message' in error ? error.message : 'Failed to upload image');
    }
  });

  const uploadImage = useCallback(
    async (files: File[]): Promise<void> => {
      if (!files.length) {
        return;
      }

      setIsAddingImage(true);

      try {
        await uploadFiles(files);
      } catch (error) {
        logger.error(error);
        showErrorToast('Failed to upload image');
      } finally {
        setIsAddingImage(false);
        onUploadComplete?.();
      }
    },
    [uploadFiles, onUploadComplete, logger]
  );

  const addImageOverlayMenuItem: AddMenuItem = useMemo(
    () => ({
      id: 'add-image-overlay',
      label: isAddingImage ? 'Adding Image...' : 'Add Image',
      isDisabled: isAddingImage,
      iconName: 'IconPhoto',
      onClick: async () => {
        return new Promise<void>((resolve, reject) => {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = acceptedFormats;
          input.multiple = false;

          input.onchange = async e => {
            try {
              const files = Array.from((e.target as HTMLInputElement).files || []);
              await uploadImage(files);
              resolve();
            } catch (error) {
              reject(error);
            }
          };

          input.click();
        });
      }
    }),
    [uploadImage, acceptedFormats, isAddingImage]
  );

  return {
    uploadImage,
    acceptedFormats,
    addImageOverlayMenuItem
  };
};
